/**
 * Browser Notifications Service
 *
 * Handles browser push notifications with sound for the web app.
 * - Requests notification permission on initialization
 * - Shows browser notifications (works even when on different tab)
 * - Plays notification sound
 * - Integrates with Supabase realtime for instant notifications
 */

import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';

// Configuration
const NOTIFICATION_SOUND_PATH = '/sounds/notification.mp3';
const APP_NAME = 'Find My Filmmaker';
const APP_ICON = '/android-chrome-192x192.png'; // High-res app icon for notifications

// State
let notificationChannel: RealtimeChannel | null = null;
let messagesChannel: RealtimeChannel | null = null;
let audioElement: HTMLAudioElement | null = null;
let isInitialized = false;
let currentUserId: string | null = null;

/**
 * Permission status
 */
export type NotificationPermission = 'granted' | 'denied' | 'default';

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission as NotificationPermission;
}

/**
 * Check if browser supports notifications
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window;
}

/**
 * Request notification permission from user
 * Returns the permission result
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    console.warn('[BrowserNotifications] Notifications not supported in this browser');
    return 'denied';
  }

  // Already granted
  if (Notification.permission === 'granted') {
    return 'granted';
  }

  // Already denied - can't ask again
  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  try {
    const permission = await Notification.requestPermission();

    if (process.env.NODE_ENV === 'development') {
      console.log('[BrowserNotifications] Permission result:', permission);
    }

    return permission as NotificationPermission;
  } catch (error) {
    console.error('[BrowserNotifications] Error requesting permission:', error);
    return 'denied';
  }
}

/**
 * Initialize audio element for notification sound
 * Creates a persistent audio element attached to the document
 */
function initAudio(): void {
  if (audioElement) return;

  // Create audio element and attach to document body for persistence
  audioElement = document.createElement('audio');
  audioElement.id = 'notification-sound';
  audioElement.src = NOTIFICATION_SOUND_PATH;
  audioElement.volume = 0.5; // 50% volume
  audioElement.preload = 'auto';

  // Hide but keep in DOM
  audioElement.style.display = 'none';
  document.body.appendChild(audioElement);

  // Preload the audio
  audioElement.load();

  console.log('[BrowserNotifications] Audio element created and attached to DOM');
}

/**
 * Play notification sound
 */
export async function playNotificationSound(): Promise<void> {
  // Check if audio element exists in DOM, recreate if needed
  let audio = document.getElementById('notification-sound') as HTMLAudioElement;

  if (!audio) {
    initAudio();
    audio = document.getElementById('notification-sound') as HTMLAudioElement;
  }

  if (!audio) {
    console.log('[BrowserNotifications] Could not create audio element');
    return;
  }

  try {
    // Reset to start if already playing
    audio.currentTime = 0;
    await audio.play();
    console.log('[BrowserNotifications] Sound played successfully');
  } catch (error: any) {
    // Autoplay might be blocked - this is normal if user hasn't interacted with page
    console.log('[BrowserNotifications] Could not play sound:', error.message);
  }
}

/**
 * Show a browser notification
 */
export function showBrowserNotification(
  title: string,
  options?: {
    body?: string;
    icon?: string;
    tag?: string;
    data?: any;
    onClick?: () => void;
    requireInteraction?: boolean;
  }
): Notification | null {
  if (!isNotificationSupported()) return null;

  if (Notification.permission !== 'granted') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[BrowserNotifications] Permission not granted, cannot show notification');
    }
    return null;
  }

  try {
    const notification = new Notification(title, {
      body: options?.body,
      icon: options?.icon || APP_ICON,
      tag: options?.tag, // Prevents duplicate notifications with same tag
      badge: APP_ICON,
      requireInteraction: options?.requireInteraction || false,
    });

    // Handle click
    if (options?.onClick) {
      notification.onclick = () => {
        options.onClick!();
        notification.close();
        // Focus the window
        window.focus();
      };
    } else {
      // Default click behavior - focus window
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }

    // Auto close after 5 seconds if not requiring interaction
    if (!options?.requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    return notification;
  } catch (error) {
    console.error('[BrowserNotifications] Error showing notification:', error);
    return null;
  }
}

/**
 * Show notification with sound
 */
export function showNotificationWithSound(
  title: string,
  options?: {
    body?: string;
    icon?: string;
    tag?: string;
    data?: any;
    onClick?: () => void;
  }
): void {
  // Play sound
  playNotificationSound();

  // Show browser notification
  showBrowserNotification(title, options);
}

/**
 * Handle incoming notification from database
 */
function handleNewNotification(payload: any): void {
  const notification = payload.new;

  if (!notification) {
    console.log('[BrowserNotifications] No notification data in payload');
    return;
  }

  console.log('[BrowserNotifications] Processing notification:', {
    id: notification.id,
    type: notification.type,
    title: notification.title,
    message: notification.message?.substring(0, 50),
  });

  // Build notification content
  const title = notification.title || APP_NAME;
  const body = notification.message || 'You have a new notification';

  // Check for link in data jsonb
  const linkUrl = notification.data?.link_url || notification.data?.url || null;

  // Check if tab is visible and focused
  const isTabActive = document.visibilityState === 'visible' && document.hasFocus();

  if (isTabActive) {
    // Tab is active - play sound and show toast notification
    console.log('[BrowserNotifications] Tab active, showing toast notification');
    playNotificationSound();

    // Show toast notification with info style
    toast.info(title, {
      description: body,
      duration: 5000,
      action: linkUrl ? {
        label: 'View',
        onClick: () => {
          window.location.href = linkUrl;
        },
      } : undefined,
    });
    return;
  }

  // Tab is not active - show browser notification with sound
  console.log('[BrowserNotifications] Tab not active, showing browser notification');
  showNotificationWithSound(title, {
    body,
    tag: `notification-${notification.id}`, // Prevent duplicates
    onClick: () => {
      // Navigate to relevant page
      if (linkUrl) {
        window.location.href = linkUrl;
      } else {
        // Default: go to dashboard
        window.location.href = '/dashboard';
      }
    },
  });
}

/**
 * Handle incoming chat message
 */
function handleNewMessage(payload: any, userId: string): void {
  const message = payload.new;

  if (!message) {
    console.log('[BrowserNotifications] No message data in payload');
    return;
  }

  // Don't notify for own messages
  if (message.user_id === userId || message.entity_id === userId) {
    console.log('[BrowserNotifications] Ignoring own message');
    return;
  }

  console.log('[BrowserNotifications] Processing message:', {
    id: message.id,
    from: message.user_name,
    content: message.content?.substring(0, 50),
  });

  // Build notification content
  const title = message.user_name || 'New Message';
  const body = message.content || 'You have a new message';

  // Determine recipient's role based on sender's role
  // If sender is filmmaker, recipient is client and vice versa
  const senderRole = message.entity_role;
  const recipientRole = senderRole === 'filmmaker' ? 'client' : 'filmmaker';

  // Build URL with correct dashboard path
  const linkUrl = message.room_name
    ? `/${recipientRole}/dashboard/messages?room=${message.room_name}`
    : `/${recipientRole}/dashboard/messages`;

  // Check if tab is visible and focused
  const isTabActive = document.visibilityState === 'visible' && document.hasFocus();

  if (isTabActive) {
    // Tab is active - play sound and show toast
    console.log('[BrowserNotifications] Tab active, showing message toast');
    playNotificationSound();

    toast.info(title, {
      description: body,
      duration: 5000,
      action: {
        label: 'View',
        onClick: () => {
          window.location.href = linkUrl;
        },
      },
    });
    return;
  }

  // Tab is not active - show browser notification with sound
  console.log('[BrowserNotifications] Tab not active, showing message browser notification');
  showNotificationWithSound(title, {
    body,
    tag: `message-${message.id}`,
    onClick: () => {
      window.location.href = linkUrl;
    },
  });
}

/**
 * Subscribe to notifications via broadcast channel (not postgres_changes to save DB connections)
 */
function subscribeToNotifications(userId: string): void {
  if (notificationChannel) {
    supabase.removeChannel(notificationChannel);
  }

  console.log('[BrowserNotifications] Subscribing to notifications for user:', userId);

  notificationChannel = supabase
    .channel('db-changes')
    .on('broadcast', { event: 'db-change' }, (payload) => {
      // Only process notifications table INSERT events for this user
      if (payload.payload?.table === 'notifications' &&
          payload.payload?.event === 'INSERT' &&
          payload.payload?.new?.user_id === userId) {
        console.log('[BrowserNotifications] Realtime event received via broadcast:', payload.payload);
        handleNewNotification({ new: payload.payload.new });
      }
    })
    .subscribe((status, err) => {
      console.log('[BrowserNotifications] Subscription status:', status);
      if (err) {
        console.error('[BrowserNotifications] Subscription error:', err);
      }
      if (status === 'SUBSCRIBED') {
        console.log('[BrowserNotifications] Successfully subscribed to db-changes broadcast');
      }
    });
}

/**
 * Subscribe to messages via broadcast channel for realtime chat notifications
 * Uses broadcast instead of postgres_changes to save DB connections
 */
function subscribeToMessages(userId: string): void {
  if (messagesChannel) {
    supabase.removeChannel(messagesChannel);
  }

  console.log('[BrowserNotifications] Subscribing to messages');

  messagesChannel = supabase
    .channel('db-changes-messages')
    .on('broadcast', { event: 'db-change' }, (payload) => {
      // Only process messages table INSERT events
      if (payload.payload?.table === 'messages' && payload.payload?.event === 'INSERT') {
        console.log('[BrowserNotifications] Message event received via broadcast:', payload.payload);
        handleNewMessage({ new: payload.payload.new }, userId);
      }
    })
    .subscribe((status, err) => {
      console.log('[BrowserNotifications] Messages subscription status:', status);
      if (err) {
        console.error('[BrowserNotifications] Messages subscription error:', err);
      }
      if (status === 'SUBSCRIBED') {
        console.log('[BrowserNotifications] Successfully subscribed to messages broadcast');
      }
    });
}

/**
 * Initialize the browser notification service
 * Call this once when the app loads and user is authenticated
 */
export async function initBrowserNotifications(userId?: string): Promise<void> {
  if (isInitialized && currentUserId === userId) {
    console.log('[BrowserNotifications] Already initialized for this user');
    return;
  }

  // Initialize audio
  initAudio();
  console.log('[BrowserNotifications] Audio initialized');

  // Get user ID if not provided
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    userId = user?.id;
  }

  if (!userId) {
    console.log('[BrowserNotifications] No user ID, skipping initialization');
    return;
  }

  currentUserId = userId;
  isInitialized = true;

  // Subscribe to notifications
  subscribeToNotifications(userId);

  // Subscribe to messages for chat notifications
  subscribeToMessages(userId);

  // Log permission status
  const permission = getNotificationPermission();
  console.log('[BrowserNotifications] Initialized for user:', userId);
  console.log('[BrowserNotifications] Notification permission:', permission);
}

/**
 * Cleanup browser notifications service
 */
export function cleanupBrowserNotifications(): void {
  if (notificationChannel) {
    supabase.removeChannel(notificationChannel);
    notificationChannel = null;
  }

  if (messagesChannel) {
    supabase.removeChannel(messagesChannel);
    messagesChannel = null;
  }

  // Don't remove audio element - keep it for future use
  // This avoids recreating it on every auth state change

  isInitialized = false;
  currentUserId = null;

  console.log('[BrowserNotifications] Cleaned up');
}

/**
 * Check if service is initialized
 */
export function isBrowserNotificationsInitialized(): boolean {
  return isInitialized;
}
